import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { gbp, num } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Sap } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const stockBadge = (s) => s < 1000 ? ["Low", "bg-red-50 text-red-700"] : s < 5000 ? ["Medium", "bg-amber-50 text-amber-700"] : ["Healthy", "bg-emerald-50 text-emerald-700"];

export default function Products() {
  const { meta } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  useEffect(() => { api.get("/products").then((r) => setItems(r.data)); }, []);
  const shown = useMemo(() => items.filter((p) => (cat === "all" || p.category === cat) &&
    `${p.sap_code} ${p.title} ${p.keywords}`.toLowerCase().includes(search.toLowerCase())), [items, cat, search]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Catalogue" title="Harrods product catalogue">
        <div className="rounded-lg border bg-white px-4 py-2 text-sm" data-testid="products-count"><b>{shown.length}</b> of {items.length} SAP lines</div>
      </PageHeader>
      <div className="flex flex-wrap gap-3 rounded-lg border bg-white/70 p-4">
        <div className="relative min-w-[240px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="bg-white pl-9" placeholder="Search SAP code, title or keyword…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="products-search" /></div>
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="w-56 bg-white" data-testid="products-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full min-w-[900px] text-sm" data-testid="products-table">
          <thead className="border-b bg-slate-50/80 text-left"><tr className="label-xs">
            <th className="px-4 py-3">SAP code</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Category</th>
            <th className="px-4 py-3">Pack</th><th className="px-4 py-3 text-right">Stock</th><th className="px-4 py-3 text-right">Price / pack</th><th className="px-4 py-3">Active</th>
          </tr></thead>
          <tbody>{shown.map((p) => {
            const [lbl, cls] = stockBadge(p.stock);
            return (
              <tr key={p.sap_code} className="border-b last:border-0 hover:bg-slate-50" data-testid={`product-row-${p.sap_code}`}>
                <td className="px-4 py-3"><Sap code={p.sap_code} /></td>
                <td className="max-w-md px-4 py-3"><div className="font-semibold">{p.title}</div><div className="line-clamp-1 text-xs text-slate-500" title={p.description}>{p.description}</div></td>
                <td className="px-4 py-3 text-slate-600">{p.category}</td>
                <td className="whitespace-nowrap px-4 py-3">{p.pack_qty} / {p.per}</td>
                <td className="px-4 py-3 text-right"><div className="tabular-nums">{num(p.stock)}</div><span className={`rounded px-1.5 text-[10px] font-semibold ${cls}`}>{lbl}</span></td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums">{gbp(p.price)}</td>
                <td className="px-4 py-3">{p.active ? <span className="text-emerald-700">Yes</span> : <span className="text-slate-400">No</span>}</td>
              </tr>);
          })}</tbody>
        </table>
      </div>
    </div>
  );
}
